import { Plus } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  quoteInclusionsAdminApi,
  type StandardInclusionRow,
} from "@/lib/quote-inclusions-admin";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntentButton } from "@/components/intent-button";
import { RowActions } from "@/components/row-actions";
import { useConfirm } from "@/hooks/use-confirm";

const schema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  bodyText: z.string().min(1, "Body text is required"),
  sortOrder: z.number().int(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

const BLANK: FormValues = { name: "", bodyText: "", sortOrder: 0, isDefault: false, isActive: true };

const ADMIN_LIST_KEY = ["quote-inclusions-admin"] as const;

export function QuoteInclusionsAdminPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StandardInclusionRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: [...ADMIN_LIST_KEY, page, q],
    queryFn: () => quoteInclusionsAdminApi.listAdmin(q, true, page + 1),
  });

  const upsert = useMutation({
    mutationFn: (form: FormValues) => {
      const body = {
        name: form.name,
        bodyText: form.bodyText,
        sortOrder: form.sortOrder,
        isDefault: form.isDefault,
        isActive: form.isActive,
      };
      return editing
        ? quoteInclusionsAdminApi.update(editing.standardInclusionId, body, editing.rowVersionBase64)
        : quoteInclusionsAdminApi.create(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
      setCreating(false);
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (row: StandardInclusionRow) =>
      quoteInclusionsAdminApi.remove(row.standardInclusionId, row.rowVersionBase64),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY }),
  });

  async function handleDelete(row: StandardInclusionRow) {
    const ok = await confirm({
      title: "Delete inclusion?",
      body: row.name,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) remove.mutate(row);
  }

  const cols: DataGridColumn<StandardInclusionRow>[] = [
    { id: "name", header: "Name", sortable: true, accessor: (r) => r.name },
    { id: "default", header: "Default", accessor: (r) => (r.isDefault ? "Yes" : "—") },
    { id: "active", header: "Active", accessor: (r) => (r.isActive ? "Yes" : "(inactive)") },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex justify-end pr-2">
          <RowActions
            onEdit={() => setEditing(r)}
            onDelete={() => void handleDelete(r)}
            deleteDisabled={remove.isPending}
            deleteTooltip="Delete Inclusion"
          />
        </div>
      ),
    },
  ];

  function toForm(row: StandardInclusionRow): FormValues {
    return {
      name: row.name,
      bodyText: row.bodyText,
      sortOrder: row.sortOrder,
      isDefault: row.isDefault,
      isActive: row.isActive,
    };
  }

  const drawerOpen = creating || editing !== null;

  return (
    <ListPage backTo="/admin"
      title="Quote inclusions"
      primaryAction={<IntentButton onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Inclusion</IntentButton>}
      search={{ value: q, onChange: (v) => { setQ(v); setPage(0); }, placeholder: "Search name…" }}
    >
      <DataGrid
        rows={data?.rows ?? []}
        columns={cols}
        rowKey={(r) => r.standardInclusionId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}
        title={editing ? "Edit inclusion" : "New inclusion"}
      >
        <EntityForm
          schema={schema}
          defaultValues={editing ? toForm(editing) : BLANK}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="inc-name">Name</Label>
                <Input id="inc-name" {...form.register("name")} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inc-body">Body text</Label>
                <textarea
                  id="inc-body"
                  rows={5}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  {...form.register("bodyText")}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inc-sort">Sort order</Label>
                <Input
                  id="inc-sort"
                  type="number"
                  {...form.register("sortOrder", { setValueAs: (v: string) => (v === "" ? 0 : Number(v)) })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...form.register("isDefault")} />
                Default (auto-added to new quotes)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...form.register("isActive")} />
                Active
              </label>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
